import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import cx from 'classnames';
import {
  DndContext,
  DragEndEvent,
  useSensors,
  useSensor,
  PointerSensor as DndPointerSensor,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faAngleLeft,
  faAngleRight,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  isEditingSelector,
  enabledImagesSelector,
  activeImageSelector,
  setActiveImage,
  useAppSelector,
  reorderLayers,
  disableLayer,
  setEditedSpaceId,
  setMarkerEditingSpaceId,
  setEditMode,
} from '../../../store';

import { EditLayer } from './EditLayer';
import { AddableLayers } from './AddableLayers';

import './LayersControl.scss';

export function LayersControl() {
  const dispatch = useAppDispatch();
  const map = useMap();

  const images = useAppSelector(enabledImagesSelector);
  const mainImages = images.filter((i) => !i.mainLayer);
  const activeImage = useAppSelector(activeImageSelector);
  const isEditing = useAppSelector(isEditingSelector);

  const layersMenu = useRef<HTMLDivElement>(null);
  const [layersMenuOverflow, setLayersMenuOverflow] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [isVisible, setIsVisible] = useState(mainImages.length > 1);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active?.data?.current || !over?.data?.current) return;
    if (active.id !== over.id) {
      const oldIndex = active.data.current.sortable.index;
      const newIndex = over.data.current.sortable.index;
      dispatch(reorderLayers(
        { source: oldIndex, destination: newIndex }
      ));
    }
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const afterScroll = () => {
    if (!layersMenu.current) return;

    const layers = [...layersMenu.current.querySelectorAll('span.layer')] as HTMLElement[];
    if (!layersMenuOverflow || isEditing) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      layers.forEach((l) => l.style.order = '');
      return;
    }

    setCanScrollLeft(layers.some((l) => l.style.order === '1'));
    setCanScrollRight(
      !layers
        // find layers not yet repositioned
        .filter((l) => l.style.order === '')
        // map to its left offset, overflow ones have this value reseted to the initial one
        .map((l) => l.offsetLeft)
        // check if the array is numerically sorted, if so we reached end of scroll
        .every((offset,i,offsets) => i === 0 || offset > offsets[i - 1])
    );
  };

  const scrollLayers = (direction:string) => {
    if (!layersMenu.current) return;
    const layers = [...layersMenu.current.querySelectorAll('span.layer')] as HTMLElement[];
    if (direction === 'right') {
      // find first layer not moved to the end
      const layer = layers.find((l) => l.style.order === '');
      if (layer) layer.style.order = '1';
    } else {
      const layer = layers.reverse().find((layer) => layer.style.order === '1');
      if (layer) layer.style.order = '';
    }
    afterScroll();
  };

  // To check if the layers menu overflows after resizing
  useEffect(() => {
    const layersMenuDiv = layersMenu.current;
    if (!layersMenuDiv) return;
    const resizeObserver = new ResizeObserver(() => {
      setLayersMenuOverflow(
        layersMenuDiv.scrollHeight > layersMenuDiv.clientHeight
      );
    });
    resizeObserver.observe!(layersMenuDiv);
    return () => resizeObserver.unobserve(layersMenuDiv);
  }, [isVisible]);

  // To update canScrollLeft/Right when layersMenuOverflow changes or switch to edit mode
  useEffect(() => {
    afterScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersMenuOverflow, isEditing]);

  // Custom Sensor: prevents dragging while selecting text on input elements
  class PointerSensor extends DndPointerSensor {
    static activators = [
      {
        eventName: 'onPointerDown' as const,
        handler: ({ nativeEvent:event }: { nativeEvent:MouseEvent }) => {
          const target = event.target as HTMLElement;
          if (!target) return false;
          if (target.tagName.toLowerCase() === 'input') return false;
          return true;
        },
      },
    ];
  }

  // To allow onClick events in Layer
  const sensors = useSensors(
    useSensor(
      PointerSensor,
      { activationConstraint: { distance: 10 } }
    )
  );

  return createPortal(
    <aside
      className="custom-layers-control leaflet-control leaflet-control-layers
        leaflet-control-layers-expanded"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <section
        className={cx(
          'layers-display-button', {
            'hidden': mainImages.length < 2 && !isEditing && !isVisible
          }
        )}
        onClick={toggleVisibility}
      >
        <FontAwesomeIcon icon={faLayerGroup} size='2x'/>
      </section>
      <section
        ref={layersMenu}
        className={cx(
          'leaflet-control-layers-base', {
            'hidden': !isVisible,
            'editing': isEditing,
            'overflow': !isEditing && layersMenuOverflow,
          }
        )}
      >
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={images}
          >
            {canScrollLeft && (
              <div className="left-arrow" onClick={() => scrollLayers('left')}>
                <FontAwesomeIcon icon={faAngleLeft} size='2x'/>
              </div>
            )}
            {images.map((image) => (
              <EditLayer
                key={image.id}
                image={image}
                isActive={activeImage?.id === image.id}
                setActive={() => {
                  dispatch(setActiveImage(image.id));
                  // close Editor when changing Layer
                  dispatch(setEditedSpaceId(null));
                  // Close marker editor
                  dispatch(setMarkerEditingSpaceId(null));
                  // Exit current mode if clicked a duplicate layer
                  // prevents adding marker/label/path on duplicate layers
                  if (image.duplicateOf) dispatch(setEditMode(''));
                  map.getContainer()?.focus();
                }}
                disable={() => {
                  if (mainImages.length > 1 || image.mainLayer) {
                    dispatch(disableLayer(image.id));
                  }
                }}
              />))}
            {canScrollRight && (
              <div className="right-arrow" onClick={() => scrollLayers('right')}>
                <FontAwesomeIcon icon={faAngleRight} size='2x'/>
              </div>
            )}
          </SortableContext>
        </DndContext>
        <AddableLayers />
      </section>
      <section className="leaflet-control-layers-overlays" />
    </aside>,
    // @ts-ignore
    map._controlCorners['bottomleft'],
  );
}
